/**
 * The look every Looker tile chart shares, taken from the Dashboard's
 * SpendConversionTrend so a Looker tile and the Dashboard read as one app:
 * horizontal grid only, no axis or tick lines, small grey ticks, rounded
 * tooltip, round legend swatches.
 *
 * Tick grey is #5f6368 (Google grey 700), not the trend's #8A8A8A: only
 * 700 and darker clear AA on white (palette memory).
 *
 * @module components/looker/charts/chartTheme
 */
import React from 'react';
import { shortDate } from '@/utils/shortDate';
import type { TFn } from '@/utils/channelCorrelation';
import { compactNumber, isIsoDate } from '../lookerChartData';

export const CHART_HEIGHT = 280;

export const GRID_PROPS = { strokeDasharray: '3 3', stroke: '#EDEDED', vertical: false } as const;
export const GRID_PROPS_HORIZONTAL_BARS = { strokeDasharray: '3 3', stroke: '#EDEDED', horizontal: false } as const;

export const AXIS_PROPS = {
  tick: { fontSize: 11, fill: '#5f6368' },
  axisLine: false,
  tickLine: false,
} as const;

export const TOOLTIP_PROPS = {
  contentStyle: { borderRadius: 12, border: '1px solid #EDEDED', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
  labelStyle: { color: '#202124', fontWeight: 600, marginBottom: 4 },
  itemStyle: { padding: 0 },
  cursor: { fill: 'rgba(95, 99, 104, 0.06)' },
} as const;

/** Value labels drawn on the chart itself. */
export const VALUE_LABEL_PROPS = { fontSize: 11, fill: '#3c4043' } as const;

/**
 * Recharts paints each legend LABEL in its series colour, which puts yellow
 * text on white at ~1.8:1; the swatch already carries the colour.
 */
export const LEGEND_PROPS = {
  iconType: 'circle' as const,
  iconSize: 8,
  wrapperStyle: { fontSize: 12, paddingTop: 8 },
  formatter: (value: string) => <span className="text-neutral-800">{value}</span>,
};

export const fullNumber = (v: unknown): string =>
  typeof v === 'number' ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(v ?? '');

export const compactTick = (v: unknown): string => (typeof v === 'number' ? compactNumber(v) : String(v ?? ''));

export const percentTick = (v: unknown): string => (typeof v === 'number' ? `${Math.round(v * 100)}%` : '');

/** Category ticks: a day becomes "2 Oct" / "2 ต.ค.", anything else is kept as Looker rendered it. */
export const categoryTick = (t: TFn) => (v: unknown): string => {
  const s = String(v ?? '');
  return isIsoDate(s) ? shortDate(s, t) : s;
};

/** An id usable inside url(#…): React's useId contains colons. */
export const useSvgId = (prefix: string): string => `${prefix}-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
